import * as React from 'react';
import { useIntl } from 'react-intl';
import { useRecoilState, useRecoilValue, useResetRecoilState, useSetRecoilState } from 'recoil';
import { CmdToastButton } from '@commander-services/gui-components/build/components/CmdToast/CmdToast';
import {
  activeFilterBluecoinsAtom,
  bluecoinIdAtom,
  bluecoinsAtom,
  filteredBluecoinsAtom,
  filteredBluecoinStringAtom,
  isBluecoinsOpenAtom,
  isMinimalizedBleAtom,
  selectedBluecoinsAtom,
} from '../Bluecoins/BluecoinState';
import ConfirmToaster from '../../Forms/ConfirmToaster/ConfirmToaster';
import {
  isDrawWaypointAtom,
  isEditModeAtom,
  isMinimalizedWpAtom,
  isWaypointsOpenAtom,
} from '../Waypoints/WaypointState';
import { isAllSimpleWindowsOpenAtom } from '../OnlineMapState';
import useAnalytics from '../../../hooks/useAnalytics';

export default function BluecoinsControl() {
  const { formatMessage: f } = useIntl();
  const resetBluecoins = useResetRecoilState(bluecoinsAtom);
  const resetFilteredBluecoins = useResetRecoilState(filteredBluecoinsAtom);
  const resetFilteredBluecoinString = useResetRecoilState(filteredBluecoinStringAtom);
  const resetActiveFilterBluecoins = useResetRecoilState(activeFilterBluecoinsAtom);
  const resetBluecoinId = useResetRecoilState(bluecoinIdAtom);
  const [isBluecoinsOpen, setIsBluecoinsOpen] = useRecoilState<boolean>(isBluecoinsOpenAtom);
  const [isConfirmDialogClosed, setIsConfirmDialogClosed] = React.useState(false);
  const [isMinimalizedBle, setIsMinimalizedBle] = useRecoilState<boolean>(isMinimalizedBleAtom);
  const setSelectedBluecoins = useSetRecoilState(selectedBluecoinsAtom);
  const setIsWaypointsOpen = useSetRecoilState(isWaypointsOpenAtom);
  const setIsAllSimpleWindowsOpen = useSetRecoilState<boolean>(isAllSimpleWindowsOpenAtom);
  const setIsMinimalizedWp = useSetRecoilState<boolean>(isMinimalizedWpAtom);
  const setIsDrawWaypoint = useSetRecoilState<boolean>(isDrawWaypointAtom);
  const setIsEditMode = useSetRecoilState<boolean>(isEditModeAtom);
  const isWaypointsOpen = useRecoilValue<boolean>(isWaypointsOpenAtom);
  const { trackEvent } = useAnalytics();

  const handleOpenBluecoins = (event: React.MouseEvent<HTMLButtonElement, MouseEvent>) => {
    event.preventDefault();
    trackEvent(['map', 'click', 'Click bluecoin icon on map']);
    if (isWaypointsOpen) {
      setIsConfirmDialogClosed(true);
      return;
    }
    setIsWaypointsOpen(false);

    if (isBluecoinsOpen && !isMinimalizedBle) {
      setIsBluecoinsOpen(false);
      setIsAllSimpleWindowsOpen(false);
      resetBluecoins();
      resetFilteredBluecoinString();
      resetFilteredBluecoins();
      resetBluecoinId();
      resetActiveFilterBluecoins();
      setSelectedBluecoins([]);
    } else if (isBluecoinsOpen && isMinimalizedBle) {
      setIsMinimalizedBle(false);
      setIsAllSimpleWindowsOpen(false);
    } else {
      setIsBluecoinsOpen(true);
      setIsAllSimpleWindowsOpen(false);
    }
  };

  const handleClose = (event?: React.MouseEvent<HTMLAnchorElement, MouseEvent> | KeyboardEvent) => {
    if (event) event.preventDefault();
    setIsConfirmDialogClosed(false);
  };

  const handleConfirmDialog = (event?: React.MouseEvent<HTMLAnchorElement, MouseEvent>) => {
    if (event) {
      event.preventDefault();
      setIsWaypointsOpen(false);
      setIsMinimalizedWp(false);
      setIsDrawWaypoint(false);
      setIsEditMode(false);

      if (isBluecoinsOpen) {
        setIsBluecoinsOpen(false);
        setIsAllSimpleWindowsOpen(false);
        resetBluecoins();
        resetFilteredBluecoinString();
        resetFilteredBluecoins();
        resetBluecoinId();
        resetActiveFilterBluecoins();
      } else {
        setIsBluecoinsOpen(true);
        setIsAllSimpleWindowsOpen(false);
      }
    }

    setIsConfirmDialogClosed(false);
  };
  const buttons: CmdToastButton[] = [
    {
      title: f({
        id: 'form.cancel',
      }),
      style: 'link',
      type: 'cancel',
      onClickCallback: handleClose,
    },
    {
      title: f({ id: 'map.waypoints.toast.confirmBtn' }),
      style: 'solid',
      type: 'confirm',
      onClickCallback: handleConfirmDialog,
    },
  ];

  const handleBleClassName = () => {
    if (isBluecoinsOpen && !isMinimalizedBle) {
      return 'e-button-sq text-primary';
    }

    if (isBluecoinsOpen && isMinimalizedBle) {
      return 'e-button-sq bubble-on';
    }

    return 'e-button-sq';
  };

  return (
    <>
      {isConfirmDialogClosed && (
        <ConfirmToaster
          id="waypointToast"
          title={f({ id: 'map.waypoints.toast.warningTitle' })}
          message={f({ id: 'map.waypoints.toast.warningMessage' })}
          type="warning"
          duration={null}
          onClose={handleClose}
          buttons={buttons}
        />
      )}

      <button
        onClick={handleOpenBluecoins}
        title={f({ id: 'menu.bluecoins' })}
        data-cy="controls-on-map-bluecoins"
        className={handleBleClassName()}
        type="button"
      >
        {!isBluecoinsOpen && (
          <svg width="16px" height="16px" viewBox="0 0 16 16">
            <g>
              <path
                d="M16,8 C16,12.41828 12.41828,16 8,16 C3.58172,16 0,12.41828 0,8 C0,3.58172 3.58172,0 8,0 C12.41828,0 16,3.58172 16,8 Z"
                id="Path"
                fill="#C8C8C8"
                fillRule="nonzero"
              />
              <path
                d="M2.666664,8 C2.666664,10.81096 4.84128,13.114 7.59996,13.31856 C7.82028,13.33488 8,13.15424 8,12.93332 L8,8.4 C8,8.17908 8.17908,8 8.4,8 L12.93332,8 C13.15424,8 13.33488,7.82028 13.31856,7.59996 C13.114,4.84128 10.81096,2.666664 8,2.666664 C5.05448,2.666664 2.666664,5.05448 2.666664,8 Z"
                id="Path"
                className="fill-primary"
                fill="#737373"
                fillRule="nonzero"
              />
              <path
                d="M8,4.40604 C6.01512,4.40604 4.40604,6.01512 4.40604,8 C4.40604,9.98488 6.01512,11.59396 8,11.59396 L8,11.87404 C5.86044,11.87404 4.12596,10.13956 4.12596,8 C4.12596,5.86044 5.86044,4.12596 8,4.12596 C10.13956,4.12596 11.87404,5.86044 11.87404,8 L11.59396,8 C11.59396,6.01512 9.98488,4.40604 8,4.40604 Z"
                id="Path"
                className="fill-primary"
                fill="#C8C8C8"
              />
              <path
                d="M10,8 C10,9.10456 9.10456,10 8,10 C6.89544,10 6,9.10456 6,8 C6,6.89544 6.89544,6 8,6 C9.10456,6 10,6.89544 10,8 Z"
                id="Path"
                fill="#FFFFFF"
                fillRule="nonzero"
              />
              <path
                d="M8.66668,2.666668 C8.66668,3.034856 8.3682,3.333332 8,3.333332 C7.6318,3.333332 7.33332,3.034856 7.33332,2.666668 C7.33332,2.298476 7.6318,2 8,2 C8.3682,2 8.66668,2.298476 8.66668,2.666668 Z"
                id="Path"
                fill="#FFFFFF"
                fillRule="nonzero"
              />
              <path
                d="M13.33332,12 C13.33332,12.3682 13.03484,12.66668 12.66668,12.66668 C12.29848,12.66668 12,12.3682 12,12 C12,11.6318 12.29848,11.33332 12.66668,11.33332 C13.03484,11.33332 13.33332,11.6318 13.33332,12 Z"
                id="Path"
                fill="#FFFFFF"
                fillRule="nonzero"
              />
              <path
                d="M5.06668,8 C5.06668,8.3682 4.7682,8.66668 4.4,8.66668 C4.0318,8.66668 3.733336,8.3682 3.733336,8 C3.733336,7.6318 4.0318,7.33332 4.4,7.33332 C4.7682,7.33332 5.06668,7.6318 5.06668,8 Z"
                id="Path"
                fill="#FFFFFF"
                fillRule="nonzero"
              />
            </g>
          </svg>
        )}
        {isBluecoinsOpen && (
          <svg width="16px" height="16px" viewBox="0 0 16 16">
            <g>
              <path
                d="M16,8 C16,12.41828 12.41828,16 8,16 C3.58172,16 0,12.41828 0,8 C0,3.58172 3.58172,0 8,0 C12.41828,0 16,3.58172 16,8 Z"
                fill="#D2E2FD"
              />
              <path
                d="M2.666664,8 C2.666664,10.81096 4.84128,13.114 7.59996,13.31856 C7.82028,13.33488 8,13.15424 8,12.93332 L8,8.4 C8,8.17908 8.17908,8 8.4,8 L12.93332,8 C13.15424,8 13.33488,7.82028 13.31856,7.59996 C13.114,4.84128 10.81096,2.666664 8,2.666664 C5.05448,2.666664 2.666664,5.05448 2.666664,8 Z"
                fill="#3D72C8"
              />
              <path
                d="M8,4.40604 C6.01512,4.40604 4.40604,6.01512 4.40604,8 C4.40604,9.98488 6.01512,11.59396 8,11.59396 L8,11.87404 C5.86044,11.87404 4.12596,10.13956 4.12596,8 C4.12596,5.86044 5.86044,4.12596 8,4.12596 C10.13956,4.12596 11.87404,5.86044 11.87404,8 L11.59396,8 C11.59396,6.01512 9.98488,4.40604 8,4.40604 Z"
                fill="#FFFFFF"
              />
              <path
                d="M10,8 C10,9.10456 9.10456,10 8,10 C6.89544,10 6,9.10456 6,8 C6,6.89544 6.89544,6 8,6 C9.10456,6 10,6.89544 10,8 Z"
                fill="#FFFFFF"
                fillRule="nonzero"
              />
              <path
                d="M8.66668,2.666668 C8.66668,3.034856 8.3682,3.333332 8,3.333332 C7.6318,3.333332 7.33332,3.034856 7.33332,2.666668 C7.33332,2.298476 7.6318,2 8,2 C8.3682,2 8.66668,2.298476 8.66668,2.666668 Z"
                fill="#FFFFFF"
                fillRule="nonzero"
              />
              <path
                d="M13.33332,12 C13.33332,12.3682 13.03484,12.66668 12.66668,12.66668 C12.29848,12.66668 12,12.3682 12,12 C12,11.6318 12.29848,11.33332 12.66668,11.33332 C13.03484,11.33332 13.33332,11.6318 13.33332,12 Z"
                fill="#FFFFFF"
                fillRule="nonzero"
              />
              <path
                d="M5.06668,8 C5.06668,8.3682 4.7682,8.66668 4.4,8.66668 C4.0318,8.66668 3.733336,8.3682 3.733336,8 C3.733336,7.6318 4.0318,7.33332 4.4,7.33332 C4.7682,7.33332 5.06668,7.6318 5.06668,8 Z"
                fill="#FFFFFF"
                fillRule="nonzero"
              />
            </g>
          </svg>
        )}
        <span className="sr-only">{f({ id: 'menu.bluecoins' })}</span>
      </button>
    </>
  );
}
